import React, { useMemo } from 'react';
import { Paper, Typography, Box } from '@mui/material';
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

const COLORS = ['#1976d2', '#ef6c00'];

// 억 단위 포맷
const fmtAmt = (v) => {
  if (!v) return '0';
  const n = Number(v);
  if (Math.abs(n) >= 100000000) return `${(n / 100000000).toFixed(1)}억`;
  if (Math.abs(n) >= 10000) return `${(n / 10000).toFixed(0)}만`;
  return String(n);
};

const MONTH_LABELS = ['1월','2월','3월','4월','5월','6월','7월','8월','9월','10월','11월','12월'];

const KpiReportChart = ({ targetTyp, years, reportData, viewMode }) => {

  // 월별 차트 데이터 구성
  const monthlyChartData = useMemo(() => {
    return MONTH_LABELS.map((label, idx) => {
      const month = String(idx + 1);
      const entry = { name: label };
      years.forEach((year) => {
        const list = reportData[year] || [];
        // list는 부서/본부 row들 → 해당 월 합산
        const total = list.reduce((sum, row) => {
          const val = row[month];
          return sum + (val ? Number(val) : 0);
        }, 0);
        entry[String(year)] = total;
      });
      return entry;
    });
  }, [reportData, years]);

  // 연도별 차트 데이터 (연간 합계)
  const yearlyChartData = useMemo(() => {
    return years.map((year) => {
      const list = reportData[year] || [];
      const total = list.reduce((sum, row) => {
        const val = row['total'];
        return sum + (val ? Number(val) : 0);
      }, 0);
      return { name: `${year}년`, [String(year)]: total };
    });
  }, [reportData, years]);

  const chartData = viewMode === 'monthly' ? monthlyChartData : yearlyChartData;
  const isEmpty = chartData.every((d) => years.every((y) => !d[String(y)]));

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle1" fontWeight={700} mb={2}>
        {viewMode === 'monthly' ? '월별 추이' : '연도별 비교'}
      </Typography>

      {isEmpty ? (
        <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
          데이터가 없습니다
        </Box>
      ) : viewMode === 'monthly' ? (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis tickFormatter={fmtAmt} width={70} />
            <Tooltip formatter={(v) => fmtAmt(v)} />
            <Legend />
            {years.map((year, i) => (
              <Line
                key={year}
                type="monotone"
                dataKey={String(year)}
                name={`${year}년`}
                stroke={COLORS[i]}
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis tickFormatter={fmtAmt} width={70} />
            <Tooltip formatter={(v) => fmtAmt(v)} />
            <Legend />
            {years.map((year, i) => (
              <Bar
                key={year}
                dataKey={String(year)}
                name={`${year}년`}
                fill={COLORS[i]}
                radius={[4, 4, 0, 0]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      )}
    </Paper>
  );
};

export default KpiReportChart;
